import { useState, useCallback } from 'react';
import { 
  ColumnInfo, 
  RowData, 
  ApiResponse,
  createFormatDetails,
  processRowData,
  transformRowDataForApi,
  transformApiResponseToRowData
} from '../services/apiService';

interface UseApiServiceReturn {
  // State
  isLoading: boolean;
  error: string | null;
  
  // Functions (only existing ones)
  generateFormatDetails: (columnInfo: ColumnInfo) => Promise<string | null>;
  fillRowData: (rowData: RowData) => Promise<any>;
  
  // Utility functions
  transformForApi: (rowData: any, columnConfigs: any[]) => RowData;
  transformFromApi: (apiResponse: any, columnConfigs: any[]) => any;
  
  // Clear error
  clearError: () => void;
}

/**
 * Hook to use API service functions with loading states and error handling
 * Only includes functions that actually exist in Firebase Functions
 */
export const useApiService = (): UseApiServiceReturn => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const handleApiCall = useCallback(async <T>(
    apiCall: () => Promise<ApiResponse<T>>,
    successCallback?: (data: T) => T
  ): Promise<T | null> => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await apiCall();
      
      if (response.success && response.data !== undefined) {
        const result = successCallback ? successCallback(response.data) : response.data;
        return result;
      } else {
        setError(response.error || 'Unknown error occurred');
        return null;
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unexpected error occurred';
      setError(errorMessage);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const generateFormatDetails = useCallback(async (columnInfo: ColumnInfo): Promise<string | null> => {
    return handleApiCall(
      () => createFormatDetails(columnInfo),
      (data) => data
    );
  }, [handleApiCall]);

  const fillRowData = useCallback(async (rowData: RowData): Promise<any> => {
    return handleApiCall(
      () => processRowData(rowData),
      (data) => data
    );
  }, [handleApiCall]);

  const transformForApi = useCallback((
    rowData: any,
    columnConfigs: Array<{ id: string | number; name: string; options: any }>
  ): RowData => {
    return transformRowDataForApi(rowData, columnConfigs);
  }, []);

  const transformFromApi = useCallback((
    apiResponse: any,
    columnConfigs: Array<{ id: string | number; name: string }>
  ): any => {
    return transformApiResponseToRowData(apiResponse, columnConfigs);
  }, []);

  return {
    // State
    isLoading,
    error,
    
    // Functions (only existing ones)
    generateFormatDetails,
    fillRowData,
    
    // Utility functions
    transformForApi,
    transformFromApi,
    
    // Clear error
    clearError
  };
}; 